import React, {
  DragEvent,
  memo,
  RefObject,
  useCallback,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TextArea from 'core/components/TextArea';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import {
  fieldChangedCSS,
  visitNoteAdditionalAssessmentActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';

type Props = {
  editable: boolean;
  disabled: boolean;
  sectionRef?: RefObject<HTMLDivElement>;
};

type SectionProps = Props & {
  field: FieldValues;
  isDirty: boolean;
};

function AdditionalAssessmentSectionContent({
  editable,
  disabled,
  sectionRef,
  field,
  isDirty,
}: SectionProps) {
  const textAreaClasses = useMemo(() => ({
    root: clsx({
      [fieldChangedCSS]: isDirty && editable,
    }),
  }), [editable, isDirty]);

  const wrapperClasses = useMemo(() => (clsx({
    '!mt-0': !editable && field.value?.length === 0,
  })), [editable, field.value]);

  const blockEditorDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  }, []);

  return (
    <Wrapper
      id="additionalAssessment"
      wrapperRef={sectionRef}
      className={wrapperClasses}
    >
      <TextArea
        {...field}
        trackingLabel="Additional Assessment"
        id={VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate}
        classes={textAreaClasses}
        disabled={disabled}
        actions={visitNoteAdditionalAssessmentActions}
        onDragOver={blockEditorDrop}
      />
    </Wrapper>
  );
}

const AdditionalAssessmentSectionContentMemoized = memo(
  AdditionalAssessmentSectionContent,
);

function AdditionalAssessmentSection({
  editable,
  disabled,
  sectionRef,
}: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name="additionalAssessment"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <AdditionalAssessmentSectionContentMemoized
          editable={editable}
          disabled={disabled}
          sectionRef={sectionRef}
          field={field}
          isDirty={isDirty}
        />
      )}
    />
  );
}

export default memo(AdditionalAssessmentSection);
